/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'

import {KTSVG, CompanyType as Companies} from '../../../helpers'
import {KTCard, KTCardBody, Select, Loading} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {basicLookup, projectLookup, districtLookup} from '../../../redux/slices/lookup.slice'
import {companyGetId, companyUpdate, resetRedirect} from '../../../redux/slices/company.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
  useMemoizedHandlers
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  GibInvoiceType: state.lookup.GibInvoiceType,
  CommercialEnterpriseType: state.lookup.CommercialEnterpriseType,
  Province: state.lookup.Province,
  District: state.lookup.District,
  CompanyType: state.lookup.CompanyType,
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading
})

const selectCompanyData = (state) => ({
  isRedirect: state.company.isRedirect,
  detail: state.company.detail,
  loading: state.company.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {
    GibInvoiceType,
    CommercialEnterpriseType,
    Province,
    District,
    CompanyType,
    projectLookupList,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectCompanyData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(basicLookup('CommercialEnterpriseType'))
    dispatch(basicLookup('Province'))
    dispatch(basicLookup('CompanyType'))
    dispatch(basicLookup('GibInvoiceType'))
    dispatch(projectLookup())
    dispatch(companyGetId(id))
  }, [dispatch, id])

  const [commercialEnterpriseType, setCommercialEnterpriseType] = useState()

  const handlers = useMemoizedHandlers({
    onChangeDistrict: (value) => {
      formik.setFieldValue('ProvinceId', value)
      dispatch(districtLookup({provinceId: value}))
    },
    onChangeCommercialType: (value) => {
      formik.setFieldValue('CommercialEnterpriseTypeId', value)
      setCommercialEnterpriseType(value.toString())
    },
    onClickNewItem: (arrayHelpers) => {
      arrayHelpers.push({
        Id: 0,
        NameSurname: '',
        Phone: '',
        Email: '',
        Description: '',
      })
    },
    handleSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(companyUpdate(values))
    }
  }, {
    onChangeDistrict: [dispatch],
    onChangeCommercialType: [],
    onClickNewItem: [],
    handleSubmit: [dispatch]
  })

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      Title: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      ProjectIds: Yup.array()
        .of(Yup.string())
        .min(1, intl.formatMessage({id: 'REQUIRED.GENERAL'}))
        .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    CompanyTypeIds: [Companies.Supplier],
    Name: '',
    Title: '',
    Email: '',
    Phone: '',
    Fax: '',
    Iban: '',
    Address: '',
    PostalCode: '',
    ProvinceId: null,
    DistrictId: null,
    CommercialEnterpriseTypeId: null,
    IdentityNumber: '',
    TaxOffice: '',
    TaxNumber: '',
    CompanyPeoples: [
      {
        Id: 0,
        NameSurname: '',
        Phone: '',
        Email: '',
        Description: '',
      },
    ],
    ProjectIds: [],
    GibInvoiceTypeId: null,
    InvoiceEmail: '',
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handlers.handleSubmit,
  })

  useEffect(() => {
    if (detail && detail.provinceId) {
      dispatch(districtLookup({provinceId: detail.provinceId}))
    }
    if (detail && detail.commercialEnterpriseTypeId) {
      formik.setFieldValue('CommercialEnterpriseTypeId', detail.commercialEnterpriseTypeId)
      setCommercialEnterpriseType(detail.commercialEnterpriseTypeId.toString())
    }

    if (detail) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)

        if (property === 'companyPeoples') {
          detail.companyPeoples.map((item, index) => {
            for (const invoice in item) {
              const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
              formik.setFieldValue(`CompanyPeoples[${index}][${propValArr}]`, item[invoice])
            }
          })
        } else if (property === 'companyTypes') {
          detail.companyTypes && detail.companyTypes.map((item, index) => {
            for (const invoice in item) {
              const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
              if (propValArr === 'CompanyTypeId') {
                formik.setFieldValue(`CompanyTypeIds[${index}]`, item[invoice])
              }
            }
          })
        } else if (property === 'projects') {
          detail.projects && detail.projects.map((item, index) => {
            for (const invoice in item) {
              const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
              if (propValArr === 'Id') {
                formik.setFieldValue(`ProjectIds[${index}]`, item[invoice])
              }
            }
          })
        } else {
          for (const values in formValues) {
            if (values === propVal) formik.setFieldValue(propVal, detail[property])
          }
        }
      }
    }
  }, [detail, dispatch, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.SUPPLIER'}),
      path: '/expenses/supplier',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/expenses/supplier/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
      dispatch(resetRedirect())
    }
  }, [isRedirect, navigate, pageUrl, id, dispatch])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.SUPPLIER.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          <FormikProvider value={formik}>
            {detail && (
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.NAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Name')}
                    />
                    {formik.touched.Name && formik.errors.Name && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.Name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.TITLE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Title')}
                    />
                    {formik.touched.Title && formik.errors.Title && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.Title}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'COMPANY.CREATE.PROJECTS'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={projectLookupList}
                      required
                      {...formik.getFieldProps('ProjectIds')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        item?.map((data) => newData.push(data.value))
                        formik.setFieldValue('ProjectIds', newData)
                      }}
                    />
                    {formik.touched.ProjectIds && formik.errors.ProjectIds && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.ProjectIds}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.COMPANYTYPES'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={CompanyType}
                      {...formik.getFieldProps('CompanyTypeIds')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        item?.map((data) => newData.push(data.value))
                        formik.setFieldValue('CompanyTypeIds', newData)
                      }}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.CREATE.COMMERCIALENTERPRISETYPE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={CommercialEnterpriseType}
                      {...formik.getFieldProps('CommercialEnterpriseTypeId')}
                      onChange={(item) => handlers.onChangeCommercialType(Number(item.value))}
                      defaultValue={commercialEnterpriseType}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.CREATE.IDENTITYNUMBER'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('IdentityNumber')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.CREATE.TAXOFFICE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('TaxOffice')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.CREATE.TAXNUMBER'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('TaxNumber')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.IBAN'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Iban')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.EMAIL'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Email')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.PHONE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Phone')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.FAX'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Fax')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.PROVINCE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={Province}
                      {...formik.getFieldProps('ProvinceId')}
                      onChange={(item) => handlers.onChangeDistrict(Number(item.value))}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.DISTRICT'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={District}
                      {...formik.getFieldProps('DistrictId')}
                      onChange={(item) => formik.setFieldValue('DistrictId', Number(item?.value))}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.POSTALCODE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('PostalCode')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.ADDRESS'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Address')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.GIBINVOICETYPE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={GibInvoiceType}
                      {...formik.getFieldProps('GibInvoiceTypeId')}
                      onChange={(item) =>
                        formik.setFieldValue('GibInvoiceTypeId', Number(item?.value))
                      }
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'COMPANY.UPDATE.INVOICEEMAIL'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('InvoiceEmail')}
                    />
                  </div>
                </div>
                <FieldArray
                  name='CompanyPeoples'
                  render={(arrayHelpers) => (
                    <>
                      <div className='table-responsive'>
                        <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                          <thead>
                            <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                              <th>
                                {intl.formatMessage({
                                  id: 'COMPANY.CREATE.COMPANYPEOPLE.NAMESURNAME',
                                })}
                              </th>
                              <th>
                                {intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.PHONE'})}
                              </th>
                              <th>
                                {intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.EMAIL'})}
                              </th>
                              <th>
                                {intl.formatMessage({
                                  id: 'COMPANY.CREATE.COMPANYPEOPLE.DESCRIPTION',
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formik.values.CompanyPeoples.map((item, index) => (
                              <tr className='border-bottom border-bottom-dashed' key={index}>
                                <td className='ps-0'>
                                  <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    autoComplete='off'
                                    disabled={formik.isSubmitting}
                                    placeholder={intl.formatMessage({
                                      id: 'COMPANY.CREATE.COMPANYPEOPLE.NAMESURNAME',
                                    })}
                                    name={`CompanyPeoples[${index}].NameSurname`}
                                    value={formik.values.CompanyPeoples[index].NameSurname}
                                    required
                                    onChange={(event) => formik.handleChange(event)}
                                  />
                                </td>
                                <td className='ps-0'>
                                  <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    autoComplete='off'
                                    disabled={formik.isSubmitting}
                                    placeholder={intl.formatMessage({
                                      id: 'COMPANY.CREATE.COMPANYPEOPLE.PHONE',
                                    })}
                                    name={`CompanyPeoples[${index}].Phone`}
                                    value={formik.values.CompanyPeoples[index].Phone}
                                    required
                                    onChange={(event) => formik.handleChange(event)}
                                  />
                                </td>
                                <td className='ps-0'>
                                  <input
                                    type='email'
                                    className='form-control form-control-solid'
                                    autoComplete='off'
                                    disabled={formik.isSubmitting}
                                    placeholder={intl.formatMessage({
                                      id: 'COMPANY.CREATE.COMPANYPEOPLE.EMAIL',
                                    })}
                                    name={`CompanyPeoples[${index}].Email`}
                                    value={formik.values.CompanyPeoples[index].Email}
                                    required
                                    onChange={(event) => formik.handleChange(event)}
                                  />
                                </td>
                                <td className='ps-0'>
                                  <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    autoComplete='off'
                                    disabled={formik.isSubmitting}
                                    placeholder={intl.formatMessage({
                                      id: 'COMPANY.CREATE.COMPANYPEOPLE.DESCRIPTION',
                                    })}
                                    name={`CompanyPeoples[${index}].Description`}
                                    value={formik.values.CompanyPeoples[index].Description}
                                    required
                                    onChange={(event) => formik.handleChange(event)}
                                  />
                                </td>

                                <td className='pt-5 text-end'>
                                  <div className='d-flex'>
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-icon btn-active-color-primary'
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      <KTSVG
                                        path='/images/icons/duotune/general/gen027.svg'
                                        className='svg-icon-2'
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <button
                        type='button'
                        className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                        onClick={() => handlers.onClickNewItem(arrayHelpers)}
                      >
                        <KTSVG
                          path='/images/icons/duotune/arrows/arr009.svg'
                          className='svg-icon-2'
                        />
                        {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                      </button>
                    </>
                  )}
                />
                <div className='row pt-15'>
                  <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      className='btn btn-light'
                      onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                    >
                      {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </FormikProvider>
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
